import FixaTabs, {
  FixaTab,
} from "@coworker/apprestructured/src/shared/wrappers/FixaTabs/FixaTabs";
import { MediaTypes } from "../constants";
import { getMediaTypeText } from "../tools";
import { useTranslation } from "@coworker/locales";
import { AlertResponse, Media } from "../types/media";
import { OngoingTaskLinksResponse } from "../types/taskLink";
import { useFilteredMediaCounts } from "../hooks/useFilteredMediaCounts";
import { FixaSkeleton } from "@coworker/apprestructured/src/shared/wrappers/FixaSkeleton/FixaSkeleton";
import styled from "styled-components";
import { FilterValue } from "@coworker/apprestructured/src/shared/filters/types/filter";
import { ArticleWithMediaCount } from "../types/article";

const SkeletonWrapper = styled.div`
  width: 100%;
  @media screen and (min-width: 1200px) {
    width: 700px;
  }
`;

const StyledTabs = styled(FixaTabs)`
  .tabs__list {
    margin-left: 16px;
  }
`;

interface MediasViewTabsProps {
  onTabChange: (tabId: string) => void;
  activeTab: string;
  allMedias: Media[];
  filterValues: FilterValue[];
  query: string;
  alerts?: AlertResponse[] | undefined;
  tasks?: OngoingTaskLinksResponse;
  loading?: boolean;
  articlesFromHfb?: ArticleWithMediaCount[] | null;
}

const countMediaTypes = (
  medias: Media[] = []
): Partial<Record<MediaTypes, number>> => {
  return medias.reduce(
    (acc, media) => {
      const type = media.mediaType as MediaTypes;
      acc[type] = (acc[type] ?? 0) + 1;
      return acc;
    },
    {} as Partial<Record<MediaTypes, number>>
  );
};

export const MediasViewTabs = ({
  onTabChange,
  activeTab,
  allMedias,
  filterValues,
  query,
  alerts,
  tasks,
  loading,
  articlesFromHfb,
}: MediasViewTabsProps) => {
  const { t } = useTranslation();
  const MEDIA_TYPE_ORDER: MediaTypes[] = [
    // Home furnishing solutions
    MediaTypes.HOME,
    MediaTypes.ROOM_SETTINGS,
    MediaTypes.VIGNETTE,
    // Compare and choose
    MediaTypes.COMPACT,
    MediaTypes.ALL_PARTS_STUDIO,
    // Combine, coordinate and revitalize
    MediaTypes.CROSS_OVER,
    MediaTypes.SHOW_CASE,
    MediaTypes.COORDINATION_MEDIA,
    MediaTypes.VITALITY,
    // Irresistible offer
    MediaTypes.PERIMETER_BREAK,
    MediaTypes.STATEMENT_MEDIA,
  ];

  const handleTabChange = (tabId: string) => {
    onTabChange(tabId);
  };

  const filteredMedias = useFilteredMediaCounts(
    allMedias,
    filterValues,
    query,
    alerts,
    tasks,
    articlesFromHfb
  );
  const totalFilteredCount = filteredMedias.length;

  const mediaTypesAndCount = countMediaTypes(allMedias || []);

  const filteredCountByType = filteredMedias.reduce(
    (acc, media) => {
      const type = media.mediaType as MediaTypes;
      acc[type] = (acc[type] ?? 0) + 1;
      return acc;
    },
    {} as Partial<Record<MediaTypes, number>>
  );

  const hasActiveFilters =
    query.length > 1 ||
    filterValues?.some(({ filterId, value }) => value && filterId !== "sortBy");

  const mediaTabs = [
    <FixaTab
      key={"allMedia"}
      text={`${t("allMediaString")} (${
        hasActiveFilters ? totalFilteredCount + "/" : ""
      }${allMedias.length})`}
      tabPanelId={"allMedia"}
      data-test-id={"media-tab-all"}
    />,
    ...MEDIA_TYPE_ORDER.map((mediaType) => {
      const totalCount = mediaTypesAndCount[mediaType] ?? 0;
      const filteredCount = filteredCountByType[mediaType] ?? 0;

      return (
        <FixaTab
          key={mediaType}
          text={`${getMediaTypeText(mediaType)} (${
            hasActiveFilters ? filteredCount + "/" : ""
          }${totalCount})`}
          tabPanelId={mediaType}
          data-test-id={`media-tab-${mediaType}`}
        />
      );
    }),
  ];

  if (loading)
    return (
      <SkeletonWrapper>
        <FixaSkeleton height="52px" />
      </SkeletonWrapper>
    );

  return (
    <StyledTabs
      tabs={mediaTabs}
      onTabChanged={handleTabChange}
      activeTab={activeTab}
    />
  );
};
